import { useState, useEffect, useCallback } from 'react';
import toastService from '../../../services/toastService';
import authService from '../../../services/authService';

/**
 * Custom hook for managing pentest scan operations
 */
export const usePentestScan = (pentestServer, onScanCompleted = null) => {
  const [currentScanId, setCurrentScanId] = useState(null);
  const [isPentestLoading, setIsPentestLoading] = useState(false);
  const [pentestResult, setPentestResult] = useState(null);
  const [progressLogs, setProgressLogs] = useState([]);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [siteUrl, setSiteUrl] = useState('');

  // Helper function to add progress logs with timestamps
  const addProgressLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setProgressLogs(prevLogs => [...prevLogs, { timestamp, message, type }]);
  }, []);

  // Process progress information from scan details
  const processProgressUpdate = useCallback((scanDetails) => {
    // Calculate progress percentage based on scan details
    if (scanDetails.progress !== undefined) {
      setProgressPercentage(scanDetails.progress);
    } else if (scanDetails.completedSteps !== undefined && scanDetails.totalSteps !== undefined) {
      const percentage = Math.round((scanDetails.completedSteps / scanDetails.totalSteps) * 100);
      setProgressPercentage(percentage);
    } else if (scanDetails.status === 'running') {
      // For running scans without explicit progress, show incremental progress
      setProgressPercentage(prev => {
        const increment = Math.min(5, 85 - prev); // Increment by 5% up to 85%
        const newProgress = prev + increment;
        
        // Add progress logs at certain milestones
        if (newProgress >= 25 && prev < 25) {
          addProgressLog('Scan progress: 25% - Initial reconnaissance completed');
        } else if (newProgress >= 50 && prev < 50) {
          addProgressLog('Scan progress: 50% - Port scanning in progress');
        } else if (newProgress >= 75 && prev < 75) {
          addProgressLog('Scan progress: 75% - Vulnerability detection phase');
        }
        
        return newProgress;
      });
    }

    // Log new phase if it changed
    if (scanDetails.currentPhase && !progressLogs.some(log => log.message.includes(scanDetails.currentPhase))) {
      addProgressLog(`Current phase: ${scanDetails.currentPhase}`);
    }
    
    // Log any findings or vulnerabilities discovered
    if (scanDetails.findings && Array.isArray(scanDetails.findings)) {
      const newFindings = scanDetails.findings.filter(finding => 
        !progressLogs.some(log => log.message.includes(finding.id || finding.name))
      );
      
      newFindings.forEach(finding => {
        addProgressLog(`Found vulnerability: ${finding.name || finding.type} - Severity: ${finding.severity || 'Unknown'}`, 
          finding.severity === 'high' ? 'error' : finding.severity === 'medium' ? 'warning' : 'info');
      });
    }

    // Log debug information if available
    if (scanDetails.debugInfo) {
      addProgressLog(`Debug info: ${scanDetails.debugInfo}`, 'debug');
    }
  }, [progressLogs, addProgressLog]);

  // Start a new pentest scan
  const startScan = useCallback(async (siteUrl, userInfo = null, selectedTools = []) => {
    if (!siteUrl) {
      toastService.warning('Please enter a site address.');
      return;
    }

    setIsPentestLoading(true);
    setPentestResult(null);
    setCurrentScanId(null);
    setProgressLogs([]);
    setProgressPercentage(0);

    // Add initial log entry
    addProgressLog('Starting pentest scan...');
    addProgressLog('Debug mode enabled for detailed scanning');
    if (userInfo) {
      addProgressLog(`Scan initiated by user: ${userInfo.username} (${userInfo.role})`);
    }

    try {
      addProgressLog(`Initiating scan for target: ${siteUrl}`);
      
      // Prepare scan request with selected tools
      const scanRequest = {
        targetUrl: siteUrl,
        scanOptions: { 
          debug: true,
          tools: selectedTools.length > 0 ? selectedTools : undefined
        }
      };
      
      // User information is now extracted from Authorization header on server side
      if (userInfo) {
        console.log('usePentestScan: User info available (will be extracted from auth header on server):', userInfo.username);
      } else {
        console.log('usePentestScan: No user info provided (server will extract from auth header)');
      }
      
      console.log('usePentestScan: Full scan request:', JSON.stringify(scanRequest, null, 2));
      
      const response = await authService.authenticatedCall(`${pentestServer}/scan/start`, {
        method: 'POST',
        body: JSON.stringify(scanRequest),
      });

      const resultData = await response.json();
      if (!response.ok) {
        throw new Error(resultData.message || `HTTP error! status: ${response.status}`);
      }

      if (resultData && resultData.scanId) {
        setCurrentScanId(resultData.scanId);
        addProgressLog(`Scan initiated successfully. Scan ID: ${resultData.scanId}`);
        addProgressLog('Beginning vulnerability detection with debug mode enabled...');
      } else {
        throw new Error('Scan ID not received from server.');
      }
    } catch (error) {
      console.error("Could not run pentest:", error);
      addProgressLog(`Error: ${error.message}`, 'error');
      setPentestResult({ error: true, message: error.message });
      setIsPentestLoading(false);
    }
  }, [pentestServer, addProgressLog]);

  // Poll scan status
  useEffect(() => {
    let intervalId;

    if (currentScanId && isPentestLoading) {
      const pollScanStatus = async () => {
        try {
          const response = await authService.authenticatedCall(`${pentestServer}/scan/status/${currentScanId}`);
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
          }
          const scanDetails = await response.json();

          setPentestResult(scanDetails);
          processProgressUpdate(scanDetails);

          if (scanDetails.status === 'completed' || scanDetails.status === 'failed' || scanDetails.error) {
            if (scanDetails.status === 'completed') {
              addProgressLog('Scan completed successfully!', 'success');
              setProgressPercentage(100);
              
              // Get detailed results for completed scan
              try {
                const logsResponse = await authService.authenticatedCall(`${pentestServer}/scan/logs/${currentScanId}`);
                if (logsResponse.ok) {
                  const logsData = await logsResponse.json();
                  if (logsData && logsData.rawLogs) {
                    addProgressLog('Debug information available from scan:', 'info');
                    logsData.rawLogs.forEach(log => {
                      if (log.message && log.message.includes('[DEBUG]')) {
                        addProgressLog(`Debug: ${log.message}`, 'debug');
                      }
                    });
                  }
                }
              } catch (resultsError) {
                console.error("Error fetching detailed logs:", resultsError);
                addProgressLog(`Error fetching scan results: ${resultsError.message}`, 'error');
              }
              
              // Trigger table refresh callback if provided (with small delay to ensure DB is updated)
              if (onScanCompleted && typeof onScanCompleted === 'function') {
                console.log('usePentestScan: Scan completed, triggering table refresh in 1 second...');
                setTimeout(async () => {
                  try {
                    console.log('usePentestScan: Calling onScanCompleted callback...');
                    await onScanCompleted();
                    addProgressLog('Scan results updated in database', 'info');
                    console.log('usePentestScan: Table refresh completed successfully');
                  } catch (refreshError) {
                    console.error("Error refreshing table data:", refreshError);
                    addProgressLog(`Error refreshing table: ${refreshError.message}`, 'error');
                  }
                }, 1000); // 1 second delay to ensure server has saved the results
              } else {
                console.log('usePentestScan: No onScanCompleted callback provided');
              }
            } else if (scanDetails.status === 'failed') {
              addProgressLog(`Scan failed: ${scanDetails.message || 'Unknown error'}`, 'error');
            }
            
            setIsPentestLoading(false);
            setCurrentScanId(null);
            clearInterval(intervalId);
          }
        } catch (error) {
          console.error("Could not poll scan status:", error);
          addProgressLog(`Polling error: ${error.message}`, 'error');
          setPentestResult({ error: true, message: `Polling error: ${error.message}` });
          setIsPentestLoading(false);
          setCurrentScanId(null);
          clearInterval(intervalId);
        }
      };

      pollScanStatus();
      intervalId = setInterval(pollScanStatus, 3000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [currentScanId, isPentestLoading, pentestServer, processProgressUpdate, addProgressLog, onScanCompleted]);

  // Form submission handler that prevents default behavior
  const handlePentestSubmit = useCallback(async (e, userInfo = null, selectedTools = []) => {
    e.preventDefault();
    await startScan(siteUrl, userInfo, selectedTools);
  }, [startScan, siteUrl]);

  return {
    scanId: currentScanId,
    isPentestLoading,
    pentestResult,
    progressLogs,
    progressPercentage,
    startScan,
    handlePentestSubmit,
    addProgressLog,
    siteUrl,
    setSiteUrl,
  };
};